import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Container,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Article as ArticleIcon,
  Business as BusinessIcon,
  Feed as FeedIcon,
  Language as LanguageIcon,
  Public as PublicIcon,
  Refresh as RefreshIcon,
  ReportProblem as ReportProblemIcon,
  RssFeed as RssFeedIcon,
  Schedule as ScheduleIcon,
  SpaceDashboard as DashboardIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { getContentCompletionOverview } from '../../api/contentCompletion';
import { getScheduledJobStatus } from '../../api/scheduledJobs';
import { CompanyContentHealth } from '../../types/contentCompletion';
import LoadingSpinner from '../../components/common/LoadingSpinner/LoadingSpinner';
import ErrorMessage from '../../components/common/ErrorMessage/ErrorMessage';
import { useEnvironment } from '../../hooks/useEnvironment';
import PrCoverageGapPanel from './PrCoverageGapPanel';

// The scheduled job that sweeps companies and refreshes their completion data.
// Its last run time is what we surface as "last checked".
const COMPLETION_AGENT_JOB_ID = 'company_completion_agent';

type CorporateFilter = '' | 'public' | 'private' | 'other';
type CoverageFilter = '' | 'high' | 'medium' | 'low' | 'unset';

const CORPORATE_LABELS: Record<CorporateFilter, string> = {
  '': 'All companies',
  public: 'Public',
  private: 'Private',
  other: 'Other',
};

const COVERAGE_LABELS: Record<CoverageFilter, string> = {
  '': 'All priorities',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
  unset: 'Unset',
};

// --- Metric predicates ---------------------------------------------------
// "Getting data" is split by source: press-release content vs Quartr
// (financial) content. Both statuses are computed server-side against the
// stale window; a company may be receiving one, both, or neither.
const norm = (value?: string) => (value || '').trim().toLowerCase();
const isPublic = (c: CompanyContentHealth) => norm(c.corporate_status) === 'public';
const isPrivate = (c: CompanyContentHealth) => norm(c.corporate_status) === 'private';
const isOther = (c: CompanyContentHealth) => !isPublic(c) && !isPrivate(c);
const isCoverageHigh = (c: CompanyContentHealth) => norm(c.coverage_priority) === 'high';
const isCoverageMedium = (c: CompanyContentHealth) => norm(c.coverage_priority) === 'medium';
const isCoverageLow = (c: CompanyContentHealth) => norm(c.coverage_priority) === 'low';
const isCoverageUnset = (c: CompanyContentHealth) =>
  !isCoverageHigh(c) && !isCoverageMedium(c) && !isCoverageLow(c);
// Press-release content flowing in within the stale window.
const isGettingPr = (c: CompanyContentHealth) => c.status === 'active';
// Quartr (financial) content flowing in within the stale window. Quartr only
// tracks public companies, so this is ~0 outside the Public scope by design.
const isGettingQuartr = (c: CompanyContentHealth) => c.quartr_status === 'active';
const hasWebsite = (c: CompanyContentHealth) => Boolean(c.website && c.website.trim());
const hasPrFeed = (c: CompanyContentHealth) => c.has_feed_configured;
const hasQuartrFeedConfigured = (c: CompanyContentHealth) =>
  isPublic(c) && c.has_quartr_feed;
// "Missing" = no feed configured or a configured feed that has produced no content.
const isMissing = (c: CompanyContentHealth) =>
  c.status === 'no_feed' || c.status === 'no_content';

const formatDateTime = (value?: string | null) => {
  if (!value) return 'Never';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Never';
  return parsed.toLocaleString();
};

const relativeTime = (value?: string | null) => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  const diffMs = Date.now() - parsed.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays <= 0) return 'today';
  if (diffDays === 1) return 'yesterday';
  return `${diffDays} days ago`;
};

interface SegmentStats {
  count: number;
  gettingPr: number;
  gettingQuartr: number;
  prFeedConfigured: number;
  hasWebsite: number;
  quartrFeedConfigured: number;
  missing: number;
}

const computeStats = (list: CompanyContentHealth[]): SegmentStats => ({
  count: list.length,
  gettingPr: list.filter(isGettingPr).length,
  gettingQuartr: list.filter(isGettingQuartr).length,
  prFeedConfigured: list.filter(hasPrFeed).length,
  hasWebsite: list.filter(hasWebsite).length,
  quartrFeedConfigured: list.filter(hasQuartrFeedConfigured).length,
  missing: list.filter(isMissing).length,
});

const pct = (part: number, whole: number) =>
  whole === 0 ? '0%' : `${Math.round((part / whole) * 100)}%`;

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  caption?: string;
  color?: string;
}

const StatCard: React.FC<StatCardProps> = ({
  icon,
  label,
  value,
  caption,
  color = 'primary.main',
}) => (
  <Card variant="outlined" sx={{ flex: '1 1 180px', minWidth: 180 }}>
    <CardContent sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
      <Box
        sx={{
          color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mt: 0.25,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="h5" fontWeight={700} lineHeight={1.1}>
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary" noWrap>
          {label}
        </Typography>
        {caption && (
          <Typography variant="caption" color="text.secondary">
            {caption}
          </Typography>
        )}
      </Box>
    </CardContent>
  </Card>
);

const CompletionDashboardPage: React.FC = () => {
  const { environment } = useEnvironment();
  const [companies, setCompanies] = useState<CompanyContentHealth[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastCheckedAt, setLastCheckedAt] = useState<string | null>(null);
  const [lastCheckedStatus, setLastCheckedStatus] = useState<string | null>(null);

  const [staleDays, setStaleDays] = useState(30);
  const [corporateFilter, setCorporateFilter] = useState<CorporateFilter>('');
  const [coverageFilter, setCoverageFilter] = useState<CoverageFilter>('');
  const [refreshToken, setRefreshToken] = useState(0);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const overview = await getContentCompletionOverview({ stale_days: staleDays });
      setCompanies(overview.companies);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        (err as Error)?.message ||
        'Failed to load completion data';
      setError(msg);
    } finally {
      setLoading(false);
    }
    // "Last checked" is best-effort — never block the dashboard on it.
    try {
      const jobStatus = await getScheduledJobStatus(COMPLETION_AGENT_JOB_ID);
      setLastCheckedAt(jobStatus.last_run_at);
      setLastCheckedStatus(jobStatus.last_run_status);
    } catch {
      setLastCheckedAt(null);
      setLastCheckedStatus(null);
    }
    setRefreshToken((n) => n + 1);
    // `environment` is intentionally a dependency: the axios client reads the
    // active environment from sessionStorage at request time, so re-creating
    // fetchData on toggle is what triggers a refetch against the new env.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staleDays, environment]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Companies in the selected corporate-status + coverage-priority scope —
  // the basis for the KPI cards. Empty filters use every company.
  const scoped = useMemo(() => {
    let list = companies;
    switch (corporateFilter) {
      case 'public':
        list = list.filter(isPublic);
        break;
      case 'private':
        list = list.filter(isPrivate);
        break;
      case 'other':
        list = list.filter(isOther);
        break;
      default:
        break;
    }
    switch (coverageFilter) {
      case 'high':
        return list.filter(isCoverageHigh);
      case 'medium':
        return list.filter(isCoverageMedium);
      case 'low':
        return list.filter(isCoverageLow);
      case 'unset':
        return list.filter(isCoverageUnset);
      default:
        return list;
    }
  }, [companies, corporateFilter, coverageFilter]);

  const totals = useMemo(() => computeStats(scoped), [scoped]);
  const publicCount = useMemo(() => scoped.filter(isPublic).length, [scoped]);
  const allPublicCount = useMemo(() => companies.filter(isPublic).length, [companies]);
  const quartrFeedPctDenominator =
    corporateFilter === 'public' ? totals.count : allPublicCount;

  // The breakdown always covers every company so it stays a full comparison,
  // regardless of the selected scope (the selected row is highlighted).
  const breakdown = useMemo(
    () => [
      { key: 'public' as const, label: 'Public', stats: computeStats(companies.filter(isPublic)) },
      { key: 'private' as const, label: 'Private', stats: computeStats(companies.filter(isPrivate)) },
      { key: 'other' as const, label: 'Other', stats: computeStats(companies.filter(isOther)) },
    ],
    [companies]
  );

  const coverageBreakdown = useMemo(
    () => [
      { key: 'high' as const, label: 'High', stats: computeStats(companies.filter(isCoverageHigh)) },
      {
        key: 'medium' as const,
        label: 'Medium',
        stats: computeStats(companies.filter(isCoverageMedium)),
      },
      { key: 'low' as const, label: 'Low', stats: computeStats(companies.filter(isCoverageLow)) },
      {
        key: 'unset' as const,
        label: 'Unset',
        stats: computeStats(companies.filter(isCoverageUnset)),
      },
    ],
    [companies]
  );

  const allTotals = useMemo(() => computeStats(companies), [companies]);

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <DashboardIcon color="primary" sx={{ fontSize: 32 }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" component="h1" fontWeight={700}>
            Company Completion Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Completion stats across all companies — who is public, who is getting data, who has
            press-release feeds, websites, and Quartr feeds configured, and who is missing coverage.
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <span>
            <IconButton onClick={fetchData} disabled={loading} color="primary">
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="corporate-filter-label">Show stats for</InputLabel>
            <Select
              labelId="corporate-filter-label"
              value={corporateFilter}
              label="Show stats for"
              onChange={(e) => setCorporateFilter(e.target.value as CorporateFilter)}
            >
              <MenuItem value="">All companies</MenuItem>
              <MenuItem value="public">Public companies</MenuItem>
              <MenuItem value="private">Private companies</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="coverage-filter-label">Coverage priority</InputLabel>
            <Select
              labelId="coverage-filter-label"
              value={coverageFilter}
              label="Coverage priority"
              onChange={(e) => setCoverageFilter(e.target.value as CoverageFilter)}
            >
              <MenuItem value="">All priorities</MenuItem>
              <MenuItem value="high">High</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="low">Low</MenuItem>
              <MenuItem value="unset">Unset</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Stale after (days)"
            type="number"
            value={staleDays}
            onChange={(e) => setStaleDays(Math.max(1, parseInt(e.target.value, 10) || 30))}
            size="small"
            sx={{ width: 150 }}
            inputProps={{ min: 1, max: 365 }}
            helperText="Defines 'getting data'"
          />
        </Box>
      </Paper>

      {error && <ErrorMessage message={error} onDismiss={() => setError(null)} />}

      {loading ? (
        <LoadingSpinner message="Loading completion stats..." />
      ) : (
        <>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
            <StatCard
              icon={<BusinessIcon />}
              label={
                [
                  corporateFilter ? CORPORATE_LABELS[corporateFilter] : null,
                  coverageFilter ? COVERAGE_LABELS[coverageFilter] : null,
                  'companies',
                ]
                  .filter(Boolean)
                  .join(' ')
              }
              value={totals.count.toLocaleString()}
              caption={
                corporateFilter || coverageFilter
                  ? `${pct(totals.count, allTotals.count)} of all companies`
                  : 'in this environment'
              }
            />
            {corporateFilter === '' && (
              <StatCard
                icon={<PublicIcon />}
                label="Public companies"
                value={publicCount.toLocaleString()}
                caption={pct(publicCount, totals.count)}
                color="info.main"
              />
            )}
            <StatCard
              icon={<ArticleIcon />}
              label="Getting PR data"
              value={totals.gettingPr.toLocaleString()}
              caption={`${pct(totals.gettingPr, totals.count)} · press releases`}
              color="success.main"
            />
            <StatCard
              icon={<TrendingUpIcon />}
              label="Getting Quartr data"
              value={totals.gettingQuartr.toLocaleString()}
              caption={`${pct(totals.gettingQuartr, totals.count)} · financial events`}
              color="success.main"
            />
            <StatCard
              icon={<RssFeedIcon />}
              label="PR feed configured"
              value={totals.prFeedConfigured.toLocaleString()}
              caption={pct(totals.prFeedConfigured, totals.count)}
              color="primary.main"
            />
            <StatCard
              icon={<LanguageIcon />}
              label="Has website"
              value={totals.hasWebsite.toLocaleString()}
              caption={pct(totals.hasWebsite, totals.count)}
              color="primary.main"
            />
            <StatCard
              icon={<FeedIcon />}
              label="Quartr feed configured"
              value={totals.quartrFeedConfigured.toLocaleString()}
              caption={`${pct(totals.quartrFeedConfigured, quartrFeedPctDenominator)} · public companies`}
              color="info.main"
            />
            <StatCard
              icon={<ReportProblemIcon />}
              label="Missing coverage"
              value={totals.missing.toLocaleString()}
              caption={pct(totals.missing, totals.count)}
              color="warning.main"
            />
            <StatCard
              icon={<ScheduleIcon />}
              label="Last checked"
              value={
                <Typography component="span" variant="h6" fontWeight={700}>
                  {relativeTime(lastCheckedAt) ?? 'Never'}
                </Typography>
              }
              caption={
                lastCheckedAt
                  ? `${formatDateTime(lastCheckedAt)}${
                      lastCheckedStatus ? ` · ${lastCheckedStatus}` : ''
                    }`
                  : 'Agent has not run'
              }
              color="text.secondary"
            />
          </Box>

          <Paper variant="outlined">
            <Box sx={{ px: 2, pt: 2 }}>
              <Typography variant="subtitle1" fontWeight={700}>
                By corporate status
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Getting PR / Getting Quartr / PR feed / Website / Quartr feed / missing, broken down
                by company type. Click a row to focus the cards above.
              </Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Corporate status</TableCell>
                    <TableCell align="right">Companies</TableCell>
                    <TableCell align="right">Getting PR</TableCell>
                    <TableCell align="right">Getting Quartr</TableCell>
                    <TableCell align="right">PR feed</TableCell>
                    <TableCell align="right">Website</TableCell>
                    <TableCell align="right">Quartr feed</TableCell>
                    <TableCell align="right">Missing</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {breakdown.map((row) => (
                    <TableRow
                      key={row.key}
                      hover
                      selected={corporateFilter === row.key}
                      sx={{ cursor: 'pointer' }}
                      onClick={() =>
                        setCorporateFilter((prev) => (prev === row.key ? '' : row.key))
                      }
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {row.label}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{row.stats.count.toLocaleString()}</TableCell>
                      <TableCell align="right">
                        {row.stats.gettingPr.toLocaleString()}{' '}
                        <Typography component="span" variant="caption" color="text.secondary">
                          ({pct(row.stats.gettingPr, row.stats.count)})
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {row.stats.gettingQuartr.toLocaleString()}{' '}
                        <Typography component="span" variant="caption" color="text.secondary">
                          ({pct(row.stats.gettingQuartr, row.stats.count)})
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {row.stats.prFeedConfigured.toLocaleString()}{' '}
                        <Typography component="span" variant="caption" color="text.secondary">
                          ({pct(row.stats.prFeedConfigured, row.stats.count)})
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {row.stats.hasWebsite.toLocaleString()}{' '}
                        <Typography component="span" variant="caption" color="text.secondary">
                          ({pct(row.stats.hasWebsite, row.stats.count)})
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {row.stats.quartrFeedConfigured.toLocaleString()}{' '}
                        <Typography component="span" variant="caption" color="text.secondary">
                          ({pct(row.stats.quartrFeedConfigured, row.stats.count)})
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {row.stats.missing.toLocaleString()}{' '}
                        <Typography component="span" variant="caption" color="text.secondary">
                          ({pct(row.stats.missing, row.stats.count)})
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow selected={corporateFilter === ''}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={700}>
                        Total
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <strong>{allTotals.count.toLocaleString()}</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>{allTotals.gettingPr.toLocaleString()}</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>{allTotals.gettingQuartr.toLocaleString()}</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>{allTotals.prFeedConfigured.toLocaleString()}</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>{allTotals.hasWebsite.toLocaleString()}</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>{allTotals.quartrFeedConfigured.toLocaleString()}</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>{allTotals.missing.toLocaleString()}</strong>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <Paper variant="outlined" sx={{ mt: 3 }}>
            <Box sx={{ px: 2, pt: 2 }}>
              <Typography variant="subtitle1" fontWeight={700}>
                By coverage priority
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Same metrics broken down by coverage priority. Click a row to focus the cards above.
              </Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Coverage priority</TableCell>
                    <TableCell align="right">Companies</TableCell>
                    <TableCell align="right">Getting PR</TableCell>
                    <TableCell align="right">Getting Quartr</TableCell>
                    <TableCell align="right">PR feed</TableCell>
                    <TableCell align="right">Website</TableCell>
                    <TableCell align="right">Quartr feed</TableCell>
                    <TableCell align="right">Missing</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {coverageBreakdown.map((row) => (
                    <TableRow
                      key={row.key}
                      hover
                      selected={coverageFilter === row.key}
                      sx={{ cursor: 'pointer' }}
                      onClick={() =>
                        setCoverageFilter((prev) => (prev === row.key ? '' : row.key))
                      }
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {row.label}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{row.stats.count.toLocaleString()}</TableCell>
                      <TableCell align="right">
                        {row.stats.gettingPr.toLocaleString()}{' '}
                        <Typography component="span" variant="caption" color="text.secondary">
                          ({pct(row.stats.gettingPr, row.stats.count)})
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {row.stats.gettingQuartr.toLocaleString()}{' '}
                        <Typography component="span" variant="caption" color="text.secondary">
                          ({pct(row.stats.gettingQuartr, row.stats.count)})
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {row.stats.prFeedConfigured.toLocaleString()}{' '}
                        <Typography component="span" variant="caption" color="text.secondary">
                          ({pct(row.stats.prFeedConfigured, row.stats.count)})
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {row.stats.hasWebsite.toLocaleString()}{' '}
                        <Typography component="span" variant="caption" color="text.secondary">
                          ({pct(row.stats.hasWebsite, row.stats.count)})
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {row.stats.quartrFeedConfigured.toLocaleString()}{' '}
                        <Typography component="span" variant="caption" color="text.secondary">
                          ({pct(row.stats.quartrFeedConfigured, row.stats.count)})
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {row.stats.missing.toLocaleString()}{' '}
                        <Typography component="span" variant="caption" color="text.secondary">
                          ({pct(row.stats.missing, row.stats.count)})
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow selected={coverageFilter === ''}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={700}>
                        Total
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <strong>{allTotals.count.toLocaleString()}</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>{allTotals.gettingPr.toLocaleString()}</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>{allTotals.gettingQuartr.toLocaleString()}</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>{allTotals.prFeedConfigured.toLocaleString()}</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>{allTotals.hasWebsite.toLocaleString()}</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>{allTotals.quartrFeedConfigured.toLocaleString()}</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>{allTotals.missing.toLocaleString()}</strong>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <PrCoverageGapPanel refreshToken={refreshToken} />
        </>
      )}
    </Container>
  );
};

export default CompletionDashboardPage;
